// Dev-only stand-in for the grouped documents API (TDDS-3384) until the backend ships.
// With REACT_APP_MOCK_GROUPED_FILES=true, a flat `{ files }` response is turned into the
// grouped shape (categories guessed from file names), and reclassify/upload stay in memory.
import { DOCUMENT_CATEGORY_KEYS, isGroupedFilesResponse } from "./documentCategories";

const MOCK_MANDATORY = ["ID_DOCUMENT", "POWER_OF_ATTORNEY", "FEE_AGREEMENT"];
const MOCK_DELAY_MS = 400;

const GUESSES = [
  [/^APOC/i, "APOC"],
  [/\b(rg|cnh|cpf|identidade)\b|identidade/i, "ID_DOCUMENT"],
  [/procura/i, "POWER_OF_ATTORNEY"],
  [/honor|contrato/i, "FEE_AGREEMENT"],
  [/comprovante|resid/i, "PROOF_OF_ADDRESS"],
  [/foto|photo/i, "PHOTOGRAPHIC_EVIDENCE"],
];

// Fictitious files for the MOCK coordinator clients (their files endpoint returns an empty list).
export const sampleMockDocuments = (matterId) => [
  { id: `${matterId}-rg`, name: "RG_frente_verso.pdf", fileSize: 482133, createdDate: "2025-03-02", category: "ID_DOCUMENT" },
  { id: `${matterId}-proc`, name: "Procuracao_assinada.pdf", fileSize: 210554, createdDate: "2025-03-04", category: "POWER_OF_ATTORNEY" },
  { id: `${matterId}-foto`, name: "foto_casa_alagada.jpg", fileSize: 2481120, createdDate: "2025-03-08", category: "PHOTOGRAPHIC_EVIDENCE" },
  { id: `${matterId}-quest`, name: "questionario_2019.pdf", fileSize: 154321, createdDate: "2019-08-12", category: null, tag: "Questionnaire 2019" },
  ...Array.from({ length: 14 }, (_, index) => ({
    id: `${matterId}-scan-${index + 1}`,
    name: `digitalizacao_${String(index + 1).padStart(2, "0")}.pdf`,
    fileSize: 90000 + index * 15000,
    createdDate: `2025-02-${String(index + 1).padStart(2, "0")}`,
    category: null,
  })),
];

const store = new Map();
const stateFor = (matterId) => {
  if (!store.has(matterId)) store.set(matterId, { overrides: {} });
  return store.get(matterId);
};

export const isGroupedFilesMockEnabled = (env = process.env) =>
  env.NODE_ENV === "development" && env.REACT_APP_MOCK_GROUPED_FILES === "true";

export const guessCategory = (name) => GUESSES.find(([pattern]) => pattern.test(String(name || "")))?.[1] || null;

export const buildMockGroupedFiles = (files, matterId, { canEdit = true } = {}) => {
  const state = stateFor(matterId);
  const source = (files || []).length === 0 && /MOCK/.test(String(matterId)) ? sampleMockDocuments(matterId) : files || [];
  const docs = source.map((file) => {
    const overridden = file.id in state.overrides;
    const category = overridden ? state.overrides[file.id] : file.category ?? (file.tag ? null : guessCategory(file.name));
    // A Docrio tag that is not a category becomes its own group (key "TAG:<tag>"), like the backend.
    return { ...file, category: !category && file.tag && !overridden ? `TAG:${file.tag}` : category };
  });
  const tags = [...new Set(docs.filter((doc) => String(doc.category || "").startsWith("TAG:")).map((doc) => doc.tag))];
  return {
    categories: [
      ...DOCUMENT_CATEGORY_KEYS.map((key) => ({
        key,
        label: key,
        mandatory: MOCK_MANDATORY.includes(key),
        expected: !["OTHER", "APOC"].includes(key),
        custom: false,
        documents: docs.filter((doc) => doc.category === key),
      })),
      ...tags.map((tag) => ({
        key: `TAG:${tag}`,
        label: tag,
        mandatory: false,
        expected: false,
        custom: true,
        documents: docs.filter((doc) => doc.category === `TAG:${tag}`),
      })),
    ],
    uncategorized: docs.filter((doc) => !doc.category),
    canEdit,
  };
};

export const withGroupedFilesMock = (response, matterId, options) => {
  if (!isGroupedFilesMockEnabled() || isGroupedFilesResponse(response)) return response;
  return buildMockGroupedFiles(response?.files || [], matterId, options);
};

const later = (value) => new Promise((resolve) => setTimeout(() => resolve(value), MOCK_DELAY_MS));

export const mockUpdateFileCategory = (matterId, doc, category) => {
  stateFor(matterId).overrides[doc.id] = category || null;
  return later({ ...doc, category: category || null });
};

// Uploads always go through the real flow; in mock mode we only remember the chosen
// category for the newly created file so it lands in the right section client-side.
export const rememberMockCategory = (matterId, fileId, category) => {
  if (fileId) stateFor(matterId).overrides[fileId] = category || null;
};
